import { useState, useEffect } from "react";
import { intent, type IntentKeyword } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { Plus, Trash2, Zap, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function IntentSettingsPage() {
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState<IntentKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKeyword, setNewKeyword] = useState("");
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await intent.listKeywords();
      setKeywords(res.keywords);
    } catch (e: any) {
      setError(e.message || "Failed to load keywords");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    const kw = newKeyword.trim();
    if (!kw || kw.length < 2) {
      setFieldError("Keyword must be at least 2 characters");
      return;
    }
    setFieldError(null);
    setAdding(true);
    try {
      await intent.createKeyword(kw);
      setNewKeyword("");
      load();
    } catch (e: any) {
      setFieldError(e.message || "Failed to add keyword");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await intent.deleteKeyword(id);
      load(); // re-fetch from server — no optimistic removal to avoid stale state on failure
    } catch (e: any) {
      setError(e.message || "Failed to remove keyword");
    } finally {
      setDeletingId(null);
    }
  };

  const activeKeywords = keywords.filter((k) => k.is_active);

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center gap-4">
        <button
          onClick={() => navigate("/leads")}
          className="p-2 rounded-lg hover:bg-black/[0.04] text-slate transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Zap size={18} className="text-coral" /> Intent Keywords
          </h1>
          <p className="text-sm text-slate">
            MoreMail scans Reddit every 2 hours for posts matching these keywords
          </p>
        </div>
      </div>

      <div className="max-w-xl mx-auto p-8 space-y-6">
        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* How it works */}
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800 space-y-1">
          <p className="font-semibold">How it works</p>
          <p>
            When a Reddit post matches a keyword and scores ≥ 6/10 for buying intent,
            it appears as a lead in your Lead Finder. Approve leads to auto-create
            subscribers and trigger your intent_lead automations.
          </p>
        </div>

        {/* Add keyword */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-2">
            Add Keyword
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => { setNewKeyword(e.target.value); setFieldError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
              placeholder='e.g. "looking for email marketing tool"'
              className={`flex-1 px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 bg-paper ${
                fieldError ? "border-red-400" : "border-black/[0.08]"
              }`}
            />
            <button
              onClick={handleAdd}
              disabled={adding}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 transition-colors"
            >
              <Plus size={15} />
              {adding ? "Adding…" : "Add"}
            </button>
          </div>
          {fieldError && <p className="mt-1 text-xs text-red-500">{fieldError}</p>}
          <p className="mt-1.5 text-xs text-slate">
            {activeKeywords.length}/20 keywords active
          </p>
        </div>

        {/* Keyword list */}
        {loading ? (
          <Spinner className="py-10" />
        ) : activeKeywords.length === 0 ? (
          <div className="py-10 text-center text-slate">
            <Zap size={28} className="mx-auto mb-2 opacity-30" />
            <p className="font-semibold">No keywords yet</p>
            <p className="text-sm mt-1">Add your first keyword above to start scanning.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeKeywords.map((kw) => (
              <div
                key={kw.id}
                className="flex items-center justify-between bg-white rounded-xl border border-black/[0.06] px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-ink">{kw.keyword}</p>
                  <p className="text-xs text-slate mt-0.5">
                    {(typeof kw.platforms === "string"
                      ? JSON.parse(kw.platforms)
                      : kw.platforms
                    ).join(", ")}{" "}
                    · Added{" "}
                    {new Date(kw.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(kw.id)}
                  disabled={deletingId === kw.id}
                  className="p-1.5 rounded-lg text-slate hover:text-red-500 hover:bg-red-50 disabled:opacity-40 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tips */}
        <div className="rounded-xl border border-black/[0.06] bg-white p-4 text-sm space-y-2">
          <p className="font-semibold text-ink">Tips for good keywords</p>
          <ul className="space-y-1 text-slate text-xs">
            <li>→ Use phrases people actually type: "best email marketing tool for"</li>
            <li>→ Pain points work well: "email deliverability problems"</li>
            <li>→ Competitor comparisons: "Mailchimp alternatives"</li>
            <li>→ Industry context: "ecommerce email automation"</li>
            <li>→ Avoid single words — they generate too much noise</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
